// Shared "thick slab" container treatment used by every bento tile on the
// dashboard. Tuned for a near-black page background — the slab body is
// LIGHTER than the page so it reads as elevated by luminance contrast, with
// a hairline rim catching ambient light, a multi-step outer drop, a bright
// inner top bevel, asymmetric side bevels, and a heavy inner bottom edge.

import type { CSSProperties } from 'react';

/** Inline style for container elements. Apply alongside TILE_CLASSNAME. */
export const THICK_TILE_STYLE: CSSProperties = {
  background:
    'linear-gradient(160deg, rgba(54,62,98,0.98) 0%, rgba(38,46,82,0.96) 32%, rgba(26,32,62,0.96) 68%, rgba(18,24,48,0.98) 100%)',
  backdropFilter: 'blur(6px)',
  boxShadow: [
    // Hairline rim — catches the low ambient light around the slab silhouette
    '0 0 0 0.5px rgba(255, 255, 255, 0.06)',
    // Outer atmospheric drops — big & diffuse for the "lift"
    '0 32px 60px -18px rgba(0, 0, 0, 0.75)',
    '0 14px 28px -8px rgba(0, 0, 0, 0.55)',
    // Tight contact line right under the slab
    '0 4px 10px -2px rgba(0, 0, 0, 0.60)',
    // Top inner bevel — bright lip that reads as a machined edge
    'inset 0 1.5px 0 rgba(255, 255, 255, 0.22)',
    'inset 0 4px 3px -3px rgba(255, 255, 255, 0.10)',
    // Side bevels — directional (light from upper-left, like the gradient)
    'inset 1.5px 0 0 rgba(255, 255, 255, 0.06)',
    'inset -1px 0 0 rgba(0, 0, 0, 0.40)',
    // Inner bottom edge — heavy base so the slab sits with weight
    'inset 0 -2px 0 rgba(0, 0, 0, 0.85)',
    'inset 0 -6px 4px -4px rgba(0, 0, 0, 0.55)',
  ].join(', '),
};

/** Base Tailwind classes — use together with `THICK_TILE_STYLE`. */
export const THICK_TILE_CLASSNAME =
  'relative overflow-hidden rounded-2xl border border-dark-border';

/**
 * The shadow stack from `THICK_TILE_STYLE` without the gradient background.
 * Use this on accent-coloured banners/cards that need their own tint but
 * still want the same "thick slab" weight.
 */
export const THICK_TILE_SHADOW = [
  '0 0 0 0.5px rgba(255, 255, 255, 0.06)',
  '0 32px 60px -18px rgba(0, 0, 0, 0.75)',
  '0 14px 28px -8px rgba(0, 0, 0, 0.55)',
  '0 4px 10px -2px rgba(0, 0, 0, 0.60)',
  'inset 0 1.5px 0 rgba(255, 255, 255, 0.22)',
  'inset 0 4px 3px -3px rgba(255, 255, 255, 0.10)',
  'inset 1.5px 0 0 rgba(255, 255, 255, 0.06)',
  'inset -1px 0 0 rgba(0, 0, 0, 0.40)',
  'inset 0 -2px 0 rgba(0, 0, 0, 0.85)',
  'inset 0 -6px 4px -4px rgba(0, 0, 0, 0.55)',
].join(', ');
